import { InfoOutline } from '@app/components/various/Icons';
import { Tooltip } from '@app/components/various/Tooltip';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Cms } from '@typings';
import { useTranslation } from 'react-i18next';

import { PreviouslyTooltipItem } from './PreviouslyTooltipItem';

interface Props {
  page: Cms.Page;
}

const SNAPSHOT_ATTRIBUTES_KEYS: (keyof Cms.PageSnapshot)[] = ['name', 'slug', 'description', 'markets'];

export const PreviouslyTooltip = ({ page, children }: React.WithChildren<Props>) => {
  const { t } = useTranslation(['common', 'cms']);

  if (page.status !== 'published_with_changes' || !isDefined(page.snapshot)) {
    return children;
  }

  const differentAttributes = SNAPSHOT_ATTRIBUTES_KEYS.filter(key => {
    const originalValue = page[key];
    const snapshotValue = page.snapshot?.[key];

    return JSON.stringify(originalValue) !== JSON.stringify(snapshotValue);
  });

  if (isEmpty(differentAttributes)) {
    return children;
  }

  return (
    <div className="flex items-center gap-2">
      {children}
      <Tooltip
        content={
          <div>
            <div className="mb-2 body-4">{t('cms:published_version')}:</div>
            {differentAttributes.includes('name') && <PreviouslyTooltipItem label={t('common:title')} value={page.snapshot.name} />}
            {differentAttributes.includes('slug') && <PreviouslyTooltipItem label={t('common:uri')} value={`/${page.snapshot.slug}`} />}
            {differentAttributes.includes('description') && (
              <PreviouslyTooltipItem label={t('common:description')} value={page.snapshot.description} />
            )}
            {differentAttributes.includes('markets') && (
              <PreviouslyTooltipItem label={t('cms:market_other')} value={page.snapshot.markets.map(market => market.name).join(', ')} />
            )}
          </div>
        }
      >
        <button type="button" className="relative z-interactiveTableItem after:absolute after:-inset-2">
          <InfoOutline className="h-5 w-5" />
        </button>
      </Tooltip>
    </div>
  );
};
